import React, { useState } from "react";
import { IoIosArrowRoundBack } from "react-icons/io";
import { MdFilterAlt } from "react-icons/md";
import { MdStar, MdStarHalf, MdStarOutline } from "react-icons/md";
import Hotelfeedback from "./HotelFeedback";
import { getAllreviews, getReviewById } from "../../../api/owner/reviews";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const ReviewAndFeedback = () => {
  const { t } = useTranslation();
  const [activeTab1, setActiveTab1] = useState("hotel");
  const tabs = [
    { key: "hotel", label: t("reviewAndFeedback").tabs.hotel },
    { key: "restaurant", label: t("reviewAndFeedback").tabs.restaurant },
  ];
  const [status, setStatus] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  
  // Map display text to backend filter values
  const filterOptions = [
    { display: t("reviewAndFeedback").filters.statuses.newest, value: "" },
    { display: t("reviewAndFeedback").filters.statuses.oldest, value: "oldest" },
  ];
  
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const limit = 5;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["reviews", page, limit, searchQuery, activeTab1, status],
    queryFn: () =>
      getAllreviews({
        page,
        limit,
        type: activeTab1,
        search: searchQuery,
        filter: status,
      }),
    keepPreviousData: true,
  });

  const reviews = data?.data?.reviews;
  const pagination = data?.data?.pagination;

  const { data: reviewDetail, isLoading: isReviewDetailLoading } = useQuery({
    queryKey: ["reviewDetail", selectedReviewId],
    queryFn: () => getReviewById(selectedReviewId),
    enabled: !!selectedReviewId && isReviewModalOpen,
  });

  // Get display text for current filter value
  const getFilterDisplayText = () => {
    const selectedOption = filterOptions.find(option => option.value === status);
    return selectedOption ? selectedOption.display : t("reviewAndFeedback").filters.filter;
  };

  const handleFilterChange = (value) => {
    setStatus(value);
    setIsOpen(false);
    setPage(1); // Reset to first page when filter changes
  };

  const handleViewReview = (reviewId) => {
    setSelectedReviewId(reviewId);
    setIsReviewModalOpen(true);
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
    setSelectedReviewId(null);
  };

  return (
    <>
      <div className="flex gap-[10px] items-center">
        <div className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]">
          <IoIosArrowRoundBack size={24} className="text-[#fff]" />
        </div>
        <h2 className="text-[22px] font-medium">
          {t("reviewAndFeedback").title}
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto]">
        <div>
          <div className="flex flex-wrap gap-4 justify-between items-center mt-[21px]">
            <ul className="flex overflow-auto">
              {tabs.map((tab) => (
                <li key={tab.key}>
                  <button
                    className={`px-[16px] py-[8px] inline-flex items-center 2xl:text-lg text-base font-normal gap-[3px] text-[#18181B] hover:text-[#4CAF50] whitespace-nowrap ${
                      activeTab1 === tab.key
                        ? "!text-[#4CAF50] border-b border-[#4CAF50] font-semibold"
                        : "hover:text-[#4CAF50]"
                    }`}
                    onClick={() => {
                      setActiveTab1(tab.key);
                      setPage(1);
                      setStatus(""); // Reset filter when switching tabs
                    }}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            <div className="flex gap-[10px] items-center">
              {/* Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="py-[9px] px-[35px] bg-white text-[17px]  font-medium flex  items-center gap-[6px] rounded-[2px] border border-[#DDDDDD]"
                >
                  <MdFilterAlt />{" "}
                  {getFilterDisplayText()}
                </button>
                {isOpen && (
                  <div className="absolute z-10 mt-2 w-36 bg-white border rounded shadow text-sm font-medium">
                    {filterOptions.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => handleFilterChange(option.value)}
                        className={`px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer ${
                          status === option.value ? "bg-gray-50 font-semibold" : ""
                        }`}
                      >
                        {option.display}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Hotelfeedback
            reviews={reviews}
            isLoading={isPending}
            isError={isError}
            pagination={pagination}
            error={error}
            setPage={setPage}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onViewReview={handleViewReview}
          />
        </div>
      </div>

      <PopupModal isOpen={isReviewModalOpen} onClose={handleCloseReviewModal}>
        <div className="w-full rounded-[20px] md:p-[30px] py-[20px] px-[15px]">
          <div className="pb-[21px] border-b border-[#EEEEEE] mb-[18px]">
            <h2 className="text-[22px] font-medium">
              {t("reviewAndFeedback")?.reviewDetail?.title || "Review Details"}
            </h2>
          </div>
          <div className="h-[350px] overflow-y-auto">
            {isReviewDetailLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : reviewDetail?.data?.review ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-[17px] font-medium text-black mb-2">
                    {t("reviewAndFeedback")?.reviewDetail?.requestId || "Request ID"}
                  </h3>
                  <p className="text-[17px] text-[#757474]">
                    {reviewDetail.data.review._id}
                  </p>
                </div>
                <div>
                  <h3 className="text-[17px] font-medium text-black mb-2">
                    {t("reviewAndFeedback")?.reviewDetail?.guestName || "Guest Name"}
                  </h3>
                  <p className="text-[17px] text-[#757474]">
                    {reviewDetail.data.review.userId?.name || "N/A"}
                  </p>
                </div>
                <div>
                  <h3 className="text-[17px] font-medium text-black mb-2">
                    {t("reviewAndFeedback")?.reviewDetail?.bookingId || "Booking ID"}
                  </h3>
                  <p className="text-[17px] text-[#757474]">
                    {reviewDetail.data.review.bookingId || reviewDetail.data.review.orderId || "N/A"}
                  </p>
                </div>
                <div>
                  <h3 className="text-[17px] font-medium text-black mb-2">
                    {t("reviewAndFeedback")?.reviewDetail?.rating || "Rating"}
                  </h3>
                  <div className="flex items-center gap-[8px]">
                    <div className="flex items-center w-auto">
                      {Array(Math.floor(reviewDetail.data.review.rating || 0))
                        .fill()
                        .map((_, index) => (
                          <MdStar
                            key={`full-${index}`}
                            size={21}
                            className="text-[#FF9D00]"
                          />
                        ))}
                      {(reviewDetail.data.review.rating || 0) % 1 !== 0 && (
                        <MdStarHalf
                          size={21}
                          className="text-[#FF9D00]"
                        />
                      )}
                      {Array(5 - Math.ceil(reviewDetail.data.review.rating || 0))
                        .fill()
                        .map((_, index) => (
                          <MdStarOutline
                            key={`empty-${index}`}
                            size={21}
                            className="text-[#FF9D00]"
                          />
                        ))}
                    </div>
                    <p className="text-sm font-medium text-[#4CAF50]">
                      ({reviewDetail.data.review.rating}/5)
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-[17px] font-medium text-black mb-2">
                    {t("reviewAndFeedback")?.reviewDetail?.reviewSummary || "Review Summary"}
                  </h3>
                  <p className="text-[17px] text-[#757474]">
                    {reviewDetail.data.review.comment || t("reviewAndFeedback")?.reviewDetail?.noComment || "No comment provided"}
                  </p>
                </div>
                {reviewDetail.data.review.createdAt && (
                  <div>
                    <h3 className="text-[17px] font-medium text-black mb-2">
                      {t("reviewAndFeedback")?.reviewDetail?.date || "Date"}
                    </h3>
                    <p className="text-[17px] text-[#757474]">
                      {new Date(reviewDetail.data.review.createdAt).toLocaleDateString("en-GB", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-red-500 text-center mt-6">
                {t("reviewAndFeedback")?.reviewDetail?.error || "Failed to load review details"}
              </p>
            )}
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default ReviewAndFeedback;
